/** Maximum images per persona chat prompt (UI + API). */
export const PERSONA_CHAT_MAX_IMAGE_FILES = 5;

/** Maximum decoded size of one persona chat image, in bytes (API limit; the UI quotes it). */
export const PERSONA_CHAT_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export type PersonaChatAllowedImageMimeType =
  (typeof PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES)[number];

export const PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES_SET = new Set<string>(
  PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES,
);

/** HTML `accept` attribute value for persona chat image uploads. */
export const PERSONA_CHAT_ACCEPTED_FILE_TYPES = PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES.join(', ');

const MIME_TYPE_TO_FORMAT_LABEL: Record<PersonaChatAllowedImageMimeType, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
};

const formatConjunctionList = (items: readonly string[]): string => {
  if (items.length === 0) {
    return '';
  }

  if (items.length === 1) {
    return items[0] ?? '';
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(', ')}, and ${items.at(-1)}`;
};

/** User-facing format list derived from {@link PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES}. */
export const PERSONA_CHAT_ALLOWED_IMAGE_FORMAT_LABELS = formatConjunctionList(
  PERSONA_CHAT_ALLOWED_IMAGE_MIME_TYPES.map((mimeType) => MIME_TYPE_TO_FORMAT_LABEL[mimeType]),
);

/** Extension fallback when `File.type` is empty (persona chat uploads only). */
export const PERSONA_CHAT_IMAGE_EXTENSION_TO_MIME: Record<string, PersonaChatAllowedImageMimeType> =
  {
    '.gif': 'image/gif',
    '.jpeg': 'image/jpeg',
    '.jpg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
  };

/** i18n message templates for persona chat attachment limits (use with `t(...)`). */
export const PERSONA_CHAT_ATTACHMENT_OVERFLOW_MESSAGE =
  'Only {{count}} images can be attached per prompt.';

export const PERSONA_CHAT_DROP_GUIDANCE_SINGLE =
  'Only {{formats}} accepted. One image per prompt. Extra images are skipped.';

export const PERSONA_CHAT_DROP_GUIDANCE_MULTIPLE =
  'Only {{formats}} accepted. Up to {{count}} images per prompt. Extra images are skipped.';
